// Public (no-auth) gallery API — approved, event-linked photos only.
// Backed by GET /public/event-photos on the Event Images service.

const BASE_URL =
  process.env.REACT_APP_EVENT_IMAGES_API_URL ||
  "https://7vnhfarqxg.execute-api.us-east-1.amazonaws.com/prod";

export const S3_PHOTO_BASE = "https://s3.amazonaws.com/social-images-public/";

const THUMB_SUFFIX_RE = /\.(jpe?g|png|webp|gif)$/i;

// Small-thumbnail URL: swap the extension for `_thumb.jpg` (the
// thumbnailGen service keeps a downsized sibling for every photo).
export const buildThumbUrl = (s3Key) => {
  if (!s3Key) return "";
  return `${S3_PHOTO_BASE}${s3Key.replace(THUMB_SUFFIX_RE, "_thumb.jpg")}`;
};

export const buildOriginalUrl = (s3Key) => `${S3_PHOTO_BASE}${s3Key}`;

const get = async (url) => {
  const resp = await fetch(url);
  const data = await resp.json().catch(() => null);
  if (!resp.ok) {
    throw new Error(
      (data && data.error) || `Request failed (${resp.status})`
    );
  }
  return data;
};

/**
 * Recent events, each with a small photo preview.
 * @returns {Promise<{events: Array<{socialEventId, title, date, photoCount, photos}>}>}
 */
export const fetchRecentEventPhotos = () =>
  get(`${BASE_URL.replace(/\/$/, "")}/public/event-photos`);

/**
 * Every event that has approved photos (no homepage cap/exclusions) —
 * the "all event photos" page.
 * @returns {Promise<{events: Array<{socialEventId, title, date, photoCount, photos}>}>}
 */
export const fetchAllEventPhotos = () =>
  get(`${BASE_URL.replace(/\/$/, "")}/public/event-photos?all=1`);

/**
 * Every approved photo for one event.
 * @param {string} socialEventId
 * @returns {Promise<{event: {socialEventId, title, date, photoCount, photos}}>}
 */
export const fetchEventGallery = (socialEventId) =>
  get(
    `${BASE_URL.replace(/\/$/, "")}/public/event-photos?socialEventId=${encodeURIComponent(
      socialEventId
    )}`
  );

/**
 * Download a photo to the device. Fetches as a blob so the browser's
 * download path works on iPhone/Android too; falls back to opening the
 * image in a new tab (long-press → Save Image) if the fetch fails.
 * @param {string} s3Key
 * @param {string} [filename]
 */
export const downloadPhoto = async (s3Key, filename) => {
  const url = buildOriginalUrl(s3Key);
  const fallbackName =
    filename ||
    (s3Key || "photo").split("/").pop() ||
    "photo.jpg";
  try {
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const blob = await resp.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = fallbackName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
  } catch (e) {
    window.open(url, "_blank", "noopener");
  }
};
